import { useRef, useEffect, useState } from 'react'

import clockIcon from 'assets/images/explore-learnings/clock-icon.svg'
import linkIcon from 'assets/images/explore-learnings/link.svg'

import CustomTooltip from './CustomToolTip'
import { Skill } from './SkillTag'
import { TagGroup } from './TagGroup'

interface ResourceCardProps {
  title: string
  description: string
  link: string
  timeToComplete?: string
  skills?: Skill[]
  type?: 'small' | 'big'
  onboarding?: number
  className?: string
  isSkillPageCard?: boolean
}

export const ResourceCard = ({
  title,
  description,
  link,
  timeToComplete,
  skills,
  type = 'big',
  onboarding = 0,
  className = '',
  isSkillPageCard = false,
}: ResourceCardProps) => {
  const titleRef = useRef<HTMLSpanElement>(null)
  const descriptionRef = useRef<HTMLSpanElement>(null)
  const [isTitleTruncated, setIsTitleTruncated] = useState(false)
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false)

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        setIsTitleTruncated(titleRef.current.scrollHeight > titleRef.current.clientHeight)
      }
      if (descriptionRef.current) {
        setIsDescriptionTruncated(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight)
      }
    }

    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [title, description])

  const classes = {
    small: {
      card: 'h-[160px]',
      inner: 'min-h-[158px]',
    },
    big: {
      card: 'h-[220px]',
      inner: 'h-[218px]',
    },
  }

  const shouldShowTooltip = isTitleTruncated || isDescriptionTruncated

  return (
    <CustomTooltip title={title} description={description} shouldShow={shouldShowTooltip}>
      <div
        className={`${classes[type].card} relative gradient-border w-[352px] rounded-2xl hover:[box-shadow:0px_14px_24px_0px_#0000000F] transition-all 
        font-TCCC cursor-pointer line-clamp-2 overflow-hidden ${className}`}
        onClick={() => {
          window.open(link, '_blank')
        }}
      >
        <div className={`${classes[type].inner} bg-white rounded-[0.9rem] w-full`}>
          <div className="flex justify-between items-center bg-[#F4F2EF] rounded-[16px] p-4">
            <span ref={titleRef} className="strong-m w-[286px] line-clamp-2">
              {title}
            </span>
            <img className="size-5" src={linkIcon} alt="Link" />
          </div>
          <div className="pt-4 px-3 flex flex-col bg-white rounded-2xl">
            {(timeToComplete || onboarding === 1) && (
              <span className="mb-2 body-xs text-[#505050] flex">
                {timeToComplete && (
                  <>
                    <img src={clockIcon} alt="Clock" className="mr-2" />
                    <span>{timeToComplete}min</span>
                  </>
                )}
                {onboarding === 1 && (
                  <span className={`${timeToComplete ? 'ml-2' : ''}`}>
                    {timeToComplete && onboarding === 1 ? <span className="mr-2">|</span> : ''}Onboarding
                  </span>
                )}
              </span>
            )}
            <span
              ref={descriptionRef}
              className={`body-s text-ellipsis ${!isSkillPageCard ? 'line-clamp-2' : 'line-clamp-3'}`}
            >
              {description}
            </span>
            {skills && !isSkillPageCard && (
              <div className="absolute bottom-4 left-3">
                <TagGroup skills={skills} />
              </div>
            )}
          </div>
        </div>
      </div>
    </CustomTooltip>
  )
}
